/**
 * Mordeczka.eu - API Services
 * 
 * HTTP client configuration and API service functions.
 */

import axios from 'axios';

// API base URL from environment
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:9006';

// Axios instance with default configuration
export const apiClient = axios.create({
  baseURL: `${API_URL}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000,
});

// Response interceptor (for error handling)
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    return Promise.reject(error);
  }
);

// =============================================================================
// Types
// =============================================================================

export interface PublicWorkImage {
  image_id: string;
  position: number;
}

export interface PublicStreamingPlatform {
  id: string;
  name: string;
  logo_image_id: string | null;
}

export interface PublicAuthor {
  id: string;
  name: string;
}

export interface PublicPublisher {
  id: string;
  name: string;
}

export interface PublicBookDetails {
  authors: PublicAuthor[];
  publisher_obj: PublicPublisher | null;
  page_count: number | null;
  isbn_10: string | null;
  isbn_13: string | null;
}

export interface PublicGenre {
  id: string;
  name: string;
}

export interface PublicWorkInfo {
  id: string;
  slug: string;
  type: 'book' | 'movie' | 'series';
  title: string;
  original_title: string | null;
  release_year: number | null;
  cover_image_id: string | null;
  description: string | null;
  external_url: string | null;
  // Movie-specific fields
  director_name: string | null;
  duration_minutes: number | null;
  nationality_name: string | null;
  nationality_code: string | null;
  nationality_flag_url: string | null;
  images: PublicWorkImage[];
  streaming_platform: PublicStreamingPlatform | null;
  // Series-specific fields
  series_start_year: number | null;
  series_end_year: number | null;
  // Book-specific fields
  book_details: PublicBookDetails | null;
  // Genres
  genres: PublicGenre[];
}

export interface PublicLibraryEntry {
  id: string;
  work_id: string;
  work_slug: string;
  work_title: string;
  work_type: 'book' | 'movie' | 'series';
  original_title: string | null;
  release_year: number | null;
  finished_at: string | null;
  rating: number | null;
  review: string | null;
  page_count: number | null;
  duration_minutes: number | null;
  season_number: number | null;
  season_year: number | null;
  season_duration_minutes: number | null;
  cover_image_id: string | null;
  cinema_name: string | null;
  cinema_hall_name: string | null;
  streaming_platform_name: string | null;
  streaming_platform_logo_image_id: string | null;
}

export interface PublicLibraryResponse {
  items: PublicLibraryEntry[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface PublicStatsResponse {
  year: number | null;
  total_completed: number;
  books_completed: number;
  movies_completed: number;
  series_completed: number;
  pages_read: number;
  minutes_watched: number;
  minutes_watched_movies: number;
  minutes_watched_series: number;
}

export interface PublicMonthlyStatsItem {
  month: number;
  total_completed: number;
  books_completed: number;
  movies_completed: number;
  series_completed: number;
  pages_read: number;
  minutes_watched: number;
  minutes_watched_movies: number;
  minutes_watched_series: number;
}

export interface PublicMonthlyStatsResponse {
  year: number;
  items: PublicMonthlyStatsItem[];
}

export interface PublicYearsResponse {
  years: number[];
}

// =============================================================================
// Service Functions
// =============================================================================

export const publicApi = {
  listLibrary: async (params: {
    year?: number;
    month?: number;
    type?: string;
    work_id?: string;
    search?: string;
    rating?: number;
    page?: number;
    page_size?: number;
  }) => {
    const { data } = await apiClient.get<PublicLibraryResponse>('/public/library', { params });
    return data;
  },

  getWaitingRoom: async (params: {
    type?: string;
    page?: number;
    page_size?: number;
  }) => {
    const { data } = await apiClient.get<PublicLibraryResponse>('/public/library/waiting-room', { params });
    return data;
  },

  listYears: async () => {
    const { data } = await apiClient.get<PublicYearsResponse>('/public/library/years');
    return data;
  },

  getStats: async (year?: number, month?: number) => {
    const { data } = await apiClient.get<PublicStatsResponse>('/public/library/stats', {
      params: { year, month },
    });
    return data;
  },

  getMonthly: async (year: number) => {
    const { data } = await apiClient.get<PublicMonthlyStatsResponse>('/public/library/monthly', {
      params: { year },
    });
    return data;
  },

  getWorkDetails: async (workId: string) => {
    const { data } = await apiClient.get<PublicWorkInfo>(`/public/works/${workId}`);
    return data;
  },

  getWorkBySlug: async (slug: string) => {
    const { data } = await apiClient.get<PublicWorkInfo>(`/public/works/by-slug/${slug}`);
    return data;
  },

  getImageUrl: (imageId: string | null) => {
    if (!imageId) return undefined;
    return `${API_URL}/api/v1/public/images/${imageId}/content`;
  },

  getDetailedStats: async (year?: number, month?: number) => {
    const { data } = await apiClient.get<PublicDetailedStatsResponse>('/public/library/detailed-stats', {
      params: { year, month },
    });
    return data;
  },

  trackPageView: async (path: string, visitorId: string) => {
    await apiClient.post('/public/analytics/page-view', {
      path,
      visitor_id: visitorId,
    });
  },
};

// =============================================================================
// Detailed Stats Types
// =============================================================================

export interface PlatformStatItem {
  platform_name: string;
  series_count: number;
  seasons_count: number;
  total_minutes: number;
}

export interface PublisherStatItem {
  publisher_name: string;
  books_count: number;
  total_pages: number;
}

export interface CinemaStatItem {
  cinema_name: string;
  movies_count: number;
  total_minutes: number;
  total_ticket_cost: number;
}

export interface CinemaHallStatItem {
  cinema_name: string;
  hall_name: string;
  movies_count: number;
  total_minutes: number;
  total_ticket_cost: number;
}

export interface DirectorStatItem {
  director_name: string;
  movies_count: number;
  total_minutes: number;
}

export interface MovieGenreStatItem {
  genre_name: string;
  movies_count: number;
  total_minutes: number;
}

export interface RatingAvgItem {
  avg_rating: number;
  rated_count: number;
}

export interface TopRatedItem {
  title: string;
  work_slug: string;
  work_type: string;
  rating: number;
  finished_at: string | null;
}

export interface RatingDistribution {
  distribution: Record<number, number>;
  averages: Record<string, RatingAvgItem>;
  top_rated: TopRatedItem[];
  top_rated_by_type: Record<string, TopRatedItem[]>;
}

export interface GenreStatItem {
  genre_name: string;
  works_count: number;
}

export interface MovieNationalityStatItem {
  nationality_name: string;
  nationality_flag_url: string | null;
  movies_count: number;
  total_minutes: number;
}

export interface SeriesNationalityStatItem {
  nationality_name: string;
  nationality_flag_url: string | null;
  series_count: number;
  seasons_count: number;
  total_minutes: number;
}

export interface BookPurchaseCostItem {
  title: string;
  work_slug: string;
  price: number;
  finished_at: string | null;
}

export interface BookPurchaseCosts {
  items: BookPurchaseCostItem[];
  total_cost: number;
  books_count: number;
  avg_price: number;
}

export interface PublicDetailedStatsResponse {
  year: number | null;
  month: number | null;
  series_by_platform: PlatformStatItem[];
  books_by_publisher: PublisherStatItem[];
  movies_by_cinema: CinemaStatItem[];
  movies_by_cinema_hall: CinemaHallStatItem[];
  movies_by_director: DirectorStatItem[];
  movies_by_genre: MovieGenreStatItem[];
  movies_by_nationality: MovieNationalityStatItem[];
  series_by_nationality: SeriesNationalityStatItem[];
  rating_stats: RatingDistribution;
  genre_breakdown: GenreStatItem[];
  book_purchase_costs: BookPurchaseCosts;
}

export default apiClient;
